import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import { notificationAPI } from '../services/api';

const NotificationsContext = createContext(null);

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
  return ctx;
};

/** Derive WebSocket base URL from the REST API URL configured in env. */
const getWsBase = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  // http://host:8000/api  →  ws://host:8000
  return apiUrl.replace(/^http/, 'ws').replace(/\/api\/?$/, '');
};

export const NotificationsProvider = ({ children }) => {
  const { currentUser, userRole } = useAuth();
  const navigate = useNavigate();

  const [unreadCount, setUnreadCount] = useState(0);

  const wsRef = useRef(null);
  const listenersRef = useRef({});
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  const pollIntervalRef = useRef(null);
  const handledNotificationIdsRef = useRef(new Set());
  const handledConsultationRoomsRef = useRef(new Set());

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ----------------------------------------------------------
  // Event-emitter helpers
  // ----------------------------------------------------------

  /** Emit an event to all registered listeners for that type (and wildcard '*'). */
  const emit = useCallback((eventType, payload) => {
    (listenersRef.current[eventType] || []).forEach((fn) => {
      try { fn(payload); } catch (_) {}
    });
    (listenersRef.current['*'] || []).forEach((fn) => {
      try { fn(eventType, payload); } catch (_) {}
    });
  }, []);

  /**
   * Subscribe to a specific event type (or '*' for every event).
   * @returns unsubscribe function — call it in the useEffect cleanup.
   */
  const on = useCallback((eventType, callback) => {
    if (!listenersRef.current[eventType]) listenersRef.current[eventType] = [];
    listenersRef.current[eventType].push(callback);
    return () => {
      listenersRef.current[eventType] = (listenersRef.current[eventType] || []).filter(
        (fn) => fn !== callback
      );
    };
  }, []);

  // ----------------------------------------------------------
  // WebSocket connection
  // ----------------------------------------------------------

  const connect = useCallback(() => {
    if (!currentUser || !userRole) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const url = `${getWsBase()}/ws/notifications/${currentUser.uid}/?role=${userRole}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        // Keep-alive ping every 30 s
        const hb = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          } else {
            clearInterval(hb);
          }
        }, 30000);
        ws._heartbeat = hb;
      };

      ws.onmessage = (event) => {
        try {
          const { type: eventType, payload } = JSON.parse(event.data);
          if (eventType === 'pong') return;

          const handleConsultationReady = (consultationPayload) => {
            const roomId = consultationPayload?.room_id;
            const apptId = consultationPayload?.appointment_id;
            if (!roomId || handledConsultationRoomsRef.current.has(roomId)) {
              return;
            }

            handledConsultationRoomsRef.current.add(roomId);

            toast(
              (t) => (
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📹</span>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">Your consultation is ready!</p>
                    <button
                      onClick={() => {
                        toast.dismiss(t.id);
                        navigate(`/consultation?room=${roomId}&appointment=${apptId || ''}`);
                      }}
                      className="mt-1 text-xs font-bold text-blue-600 underline"
                    >
                      Join Consultation Now →
                    </button>
                  </div>
                </div>
              ),
              { duration: 0 }
            );

            if (!window.location.pathname.startsWith('/consultation')) {
              setTimeout(() => {
                navigate(`/consultation?room=${roomId}&appointment=${apptId || ''}`);
              }, 800);
            }
          };

          // Increment unread badge for actionable notification events
          const notifEvents = [
            'new_appointment', 'appointment_update',
            'prescription_ready', 'prescription_updated', 'payment_confirmed',
            'emergency_request', 'emergency_accepted', 'emergency_claimed',
            'consultation_ready', 'doctor_verified',
            'queue_update', 'new_doctor_registration',
          ];
          if (notifEvents.includes(eventType) && isMountedRef.current) {
            setUnreadCount((c) => c + 1);
          }

          // Toast notifications per event type
          const toastHandlers = {
            queue_update: () =>
              toast(`New patient in queue: ${payload.patient_name || 'Patient'}`, {
                duration: 5000,
                icon: '📋',
              }),
            emergency_request: () =>
              toast.error(`🚨 EMERGENCY! Severity: ${payload.severity || 'high'}`, {
                duration: 0,
              }),
            emergency_claimed: () =>
              toast(`✅ Emergency has been accepted by another doctor.`, {
                duration: 6000,
                icon: '👨‍⚕️',
              }),
            consultation_ready: () => handleConsultationReady(payload),
            emergency_accepted: () => {
              const roomId = payload.room_id;
              const apptId = payload.appointment_id;
              toast(
                (t) => (
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🚨</span>
                    <div className="flex-1">
                      <p className="font-semibold text-sm">Doctor accepted your emergency!</p>
                      <button
                        onClick={() => {
                          toast.dismiss(t.id);
                          navigate(`/consultation?room=${roomId}&appointment=${apptId}`);
                        }}
                        className="mt-1 text-xs font-bold text-red-600 underline"
                      >
                        Join Consultation Now →
                      </button>
                    </div>
                  </div>
                ),
                { duration: 0 }
              );
              // Auto-navigate after 3 seconds
              setTimeout(() => {
                navigate(`/consultation?room=${roomId}&appointment=${apptId}`);
              }, 3000);
            },
            prescription_ready: () =>
              toast(
                (t) => (
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💊</span>
                    <div className="flex-1">
                      <p className="font-semibold text-sm">
                        New prescription from Dr. {payload.doctor_name || 'your doctor'}
                      </p>
                      <button
                        onClick={() => {
                          toast.dismiss(t.id);
                          if (payload.prescription_id) {
                            navigate(`/patient/prescriptions/${payload.prescription_id}`);
                          } else {
                            navigate('/patient/prescriptions');
                          }
                        }}
                        className="mt-1 text-xs font-bold text-purple-600 underline"
                      >
                        View Prescription →
                      </button>
                    </div>
                  </div>
                ),
                { duration: 6000 }
              ),
            prescription_updated: () =>
              toast(
                (t) => (
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📝</span>
                    <div className="flex-1">
                      <p className="font-semibold text-sm">
                        Dr. {payload.doctor_name || 'your doctor'} updated your prescription
                      </p>
                      <button
                        onClick={() => {
                          toast.dismiss(t.id);
                          if (payload.prescription_id) {
                            navigate(`/patient/prescriptions/${payload.prescription_id}`);
                          } else {
                            navigate('/patient/prescriptions');
                          }
                        }}
                        className="mt-1 text-xs font-bold text-purple-600 underline"
                      >
                        Review Update →
                      </button>
                    </div>
                  </div>
                ),
                { duration: 6000 }
              ),
            payment_confirmed: () =>
              toast.success(`✅ Payment confirmed! KES ${payload.amount || ''}`, {
                duration: 5000,
              }),
            doctor_verified: () =>
              toast.success('✅ Your doctor account has been verified!', { duration: 0 }),
            doctor_rejected: () =>
              toast.error(`❌ Verification rejected: ${payload.reason || ''}`, {
                duration: 0,
              }),
            new_doctor_registration: () =>
              toast(`👨‍⚕️ New doctor registration pending review`, { duration: 5000 }),
            appointment_update: () =>
              toast(`📅 Appointment ${payload.appointment_id} status: ${payload.status}`, {
                duration: 4000,
              }),
          };

          if (toastHandlers[eventType]) toastHandlers[eventType]();

          // Deliver to page-level subscribers
          emit(eventType, payload);
        } catch (err) {
          console.warn('Notification message handling failed:', err);
        }
      };

      ws.onclose = () => {
        if (ws._heartbeat) clearInterval(ws._heartbeat);
        if (!isMountedRef.current || !currentUser) return;

        // Exponential backoff: 1 s → 2 s → 4 s … capped at 30 s
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current && currentUser) connect();
        }, delay);
      };

      ws.onerror = () => ws.close();
    } catch (_) {}
  }, [currentUser, userRole, emit, navigate]);

  const syncUnreadNotifications = useCallback(async () => {
    if (!currentUser || !userRole) return;

    try {
      const res = await notificationAPI.list(true);
      const items = Array.isArray(res.data)
        ? res.data
        : res.data?.notifications || [];

      setUnreadCount(items.length);

      items.forEach((item) => {
        if (item?.id) {
          if (handledNotificationIdsRef.current.has(item.id)) {
            return;
          }
          handledNotificationIdsRef.current.add(item.id);
        }

        if (
          item?.type === 'consultation_ready'
          && item?.room_id
          && !window.location.pathname.startsWith('/consultation')
          && !handledConsultationRoomsRef.current.has(item.room_id)
        ) {
          handledConsultationRoomsRef.current.add(item.room_id);

          toast(
            (t) => (
              <div className="flex items-center gap-3">
                <span className="text-2xl">📹</span>
                <div className="flex-1">
                  <p className="font-semibold text-sm">Your consultation is ready!</p>
                  <button
                    onClick={() => {
                      toast.dismiss(t.id);
                      navigate(`/consultation?room=${item.room_id}&appointment=${item.appointment_id || ''}`);
                    }}
                    className="mt-1 text-xs font-bold text-blue-600 underline"
                  >
                    Join Consultation Now →
                  </button>
                </div>
              </div>
            ),
            { duration: 0 }
          );

          setTimeout(() => {
            navigate(`/consultation?room=${item.room_id}&appointment=${item.appointment_id || ''}`);
          }, 800);
        }
      });
    } catch (err) {
      console.warn('Unread notification sync failed:', err);
    }
  }, [currentUser, navigate, userRole]);

  // ----------------------------------------------------------
  // (Re)connect when auth state changes
  // ----------------------------------------------------------
  useEffect(() => {
    if (currentUser && userRole) {
      syncUnreadNotifications();
      connect();

      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(() => {
        syncUnreadNotifications();
      }, 10000);
    } else {
      // Logged out — clean up
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      setUnreadCount(0);
      reconnectAttemptsRef.current = 0;
      handledNotificationIdsRef.current = new Set();
      handledConsultationRoomsRef.current = new Set();
    }

    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [currentUser, userRole, connect, syncUnreadNotifications]);

  // ----------------------------------------------------------
  // Helpers exposed to consumers
  // ----------------------------------------------------------
  const decrementUnread = useCallback(
    (n = 1) => setUnreadCount((c) => Math.max(0, c - n)),
    []
  );
  const clearUnread = useCallback(() => setUnreadCount(0), []);

  return (
    <NotificationsContext.Provider
      value={{ unreadCount, on, decrementUnread, clearUnread }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
